import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { RAG_LABEL } from './rag';
import { VisibilityOutlined as EyeIcon, LockOutlined as LockIcon, Check as CheckIcon, RadioButtonUnchecked as CircleIcon, Close as CrossIcon } from '@mui/icons-material';

// Sprint Review building blocks shared by the Summary and Details tabs.

// Sprint Review palette (approved Team view layout).
export const A = {
  surface: '#ffffff', ink: '#16231d', ink2: '#4b5a52', ink3: '#7b887f', line: '#e1e6e1', soft: '#f4f6f3',
  sage: '#6f9279', sageSoft: '#e6efe8', sageInk: '#2f5a3e',
  warn: '#b4531a', warnSoft: '#fbece1', bad: '#b3261e', badSoft: '#fbe8e6', good: '#2f7d4a', goodSoft: '#e3f2e8',
  btn: '#16231d', bau: '#c9d1cb',
};
export const TONE = {
  ok: { bg: A.goodSoft, fg: A.good }, good: { bg: A.goodSoft, fg: A.good }, warn: { bg: A.warnSoft, fg: A.warn },
  bad: { bg: A.badSoft, fg: A.bad }, none: { bg: A.sageSoft, fg: A.sageInk },
};

// ── Small building blocks ────────────────────────────────────────────────────

export const APill = ({ tone = 'none', children }) => {
  const t = TONE[tone] || TONE.none;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.25, borderRadius: 999, fontSize: 12, fontWeight: 600, bgcolor: t.bg, color: t.fg, whiteSpace: 'nowrap', '&::before': { content: '""', width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' } }}>
      {children}
    </Box>
  );
};

export const Vis = ({ lead = true, label }) => (
  <Box component="span" sx={{ display: 'inline-flex', gap: 0.6, alignItems: 'center', fontSize: 12, fontWeight: 500, color: lead ? A.sageInk : A.ink3, whiteSpace: 'nowrap' }}>
    {lead ? <EyeIcon sx={{ fontSize: 14 }} /> : <LockIcon sx={{ fontSize: 14 }} />}
    {label ?? (lead ? 'Leadership' : 'Team only')}
  </Box>
);

export const ABtn = ({ variant = 'default', children, sx, ...props }) => (
  <Box
    component="button"
    type="button"
    {...props}
    sx={{
      height: 38, px: 2, borderRadius: '8px', font: 'inherit', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
      border: `1px solid ${variant === 'primary' ? A.btn : variant === 'ghost' ? 'transparent' : A.line}`,
      bgcolor: variant === 'primary' ? A.btn : variant === 'ghost' ? 'transparent' : A.surface,
      color: variant === 'primary' ? '#fff' : variant === 'ghost' ? A.ink2 : A.ink,
      '&:hover': { filter: 'brightness(.97)' }, '&:disabled': { opacity: 0.5, cursor: 'default' },
      '&:focus-visible': { outline: `2px solid ${A.sage}`, outlineOffset: 2 },
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const Seg = ({ value, options, onChange, ariaLabel, disabled }) => (
  <Box role="group" aria-label={ariaLabel} sx={{ display: 'inline-flex', bgcolor: A.surface, border: `1px solid ${A.line}`, borderRadius: '10px', p: '3px' }}>
    {options.map(([k, label]) => (
      <Box
        key={k}
        component="button"
        type="button"
        aria-pressed={value === k}
        disabled={disabled}
        onClick={() => onChange(k)}
        sx={{ border: 0, font: 'inherit', fontWeight: 500, px: 1.75, py: 0.75, borderRadius: '7px', cursor: disabled ? 'default' : 'pointer', bgcolor: value === k ? A.btn : 'transparent', color: value === k ? '#fff' : A.ink2 }}
      >
        {label}
      </Box>
    ))}
  </Box>
);

export const Panel = ({ children, sx, ...props }) => (
  <Box component="section" {...props} sx={{ bgcolor: A.surface, border: `1px solid ${A.line}`, borderRadius: '14px', minWidth: 0, ...sx }}>{children}</Box>
);
export const Sec = ({ children, sx }) => (
  <Box sx={{ px: { xs: 2, md: 3 }, py: 2.75, '& + &': { borderTop: `1px solid ${A.line}` }, ...sx }}>{children}</Box>
);
export const SecHead = ({ title, sub, right, sx }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, alignItems: 'baseline', mb: 1.75, ...sx }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600, color: A.ink }}>{title}</Typography>
      {sub && <Typography sx={{ color: A.ink3, fontSize: 13 }}>{sub}</Typography>}
    </Box>
    {right}
  </Box>
);
export const Cap = ({ children, sx }) => <Typography component="div" sx={{ color: A.ink3, fontSize: 12.5, ...sx }}>{children}</Typography>;

export const Mark = ({ state, children }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', fontSize: 13, color: A.ink }}>
    {state === true && <CheckIcon sx={{ fontSize: 16, color: A.good }} />}
    {state === false && <CrossIcon sx={{ fontSize: 16, color: A.bad }} />}
    {state == null && <CircleIcon sx={{ fontSize: 15, color: A.ink3 }} />}
    <span>{children}</span>
  </Box>
);

export const Bars = ({ items, max, height = 54, suffix = '' }) => {
  const top = Math.max(1, max ?? Math.max(...items.map((i) => i.value || 0)));
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.25, height: height + 18, mt: 1.5 }}>
      {items.map((it, k) => (
        <Box key={k} title={`${it.label}: ${it.value ?? '—'}${suffix}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, fontSize: 11, color: A.ink3, flex: 1, maxWidth: 64 }}>
          <Box component="i" sx={{ display: 'block', width: '100%', borderRadius: '4px 4px 0 0', minHeight: 2, height: Math.max(2, ((it.value || 0) / top) * height), bgcolor: it.current ? A.sageInk : A.sage }} />
          {it.label}
        </Box>
      ))}
    </Box>
  );
};

export const MCell = ({ title, vis, children }) => (
  <Box sx={{ px: 3, py: 2.5 }}>
    <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', color: A.ink }}>{title} {vis}</Typography>
    {children}
  </Box>
);
export const BigNum = ({ value, unit }) => (
  <Typography component="div" sx={{ fontSize: 26, fontWeight: 600, letterSpacing: '-.02em', mt: 1, color: A.ink, '& small': { fontSize: 13, color: A.ink3, fontWeight: 500, letterSpacing: 0, ml: 0.4 } }}>
    {value ?? '—'}{unit && value != null && <small>{unit}</small>}
  </Typography>
);
export const Stat2 = ({ items }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', mt: 1.25 }}>
    {items.map(([v, l]) => (
      <Box key={l}><Box component="b" sx={{ fontSize: 20, fontWeight: 600, display: 'block', lineHeight: 1.2, color: A.ink }}>{v}</Box><Box component="span" sx={{ fontSize: 12.5, color: A.ink3 }}>{l}</Box></Box>
    ))}
  </Box>
);

const RAG_STYLE = {
  g: { bg: A.goodSoft, fg: A.good },
  a: { bg: '#fff1d6', fg: '#8a5a00' },
  r: { bg: A.badSoft, fg: A.bad },
};

/** Red / Amber / Green status chip. Renders nothing when there is no rating; the reason is the tooltip. */
export const RagChip = ({ rag, text }) => {
  if (!rag || rag.tone === 'n') return null;
  const t = RAG_STYLE[rag.tone];
  const chip = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.25, borderRadius: '8px', fontSize: 12, fontWeight: 600, bgcolor: t.bg, color: t.fg, whiteSpace: 'nowrap', cursor: 'default', '&::before': { content: '""', width: 7, height: 7, borderRadius: '50%', bgcolor: 'currentColor' } }}>
      {text || RAG_LABEL[rag.tone]}
    </Box>
  );
  return rag.reason ? <Tooltip title={rag.reason}>{chip}</Tooltip> : chip;
};
